import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H3 from '@base/H3/H3';
import H1 from '@base/H1/H1';
// import P2 from '@base/P2/P2';
import P1 from '@base/P1/P1';
import Button from '@base/Button/Button';
import Image from '@base/Image';
// import ChevronRight from '@icon/ChevronRight';
import { getImage } from '@utils/imageHelper';
import AppInstallBanner from '@comp/AppInstallBanner/Version1';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import usePurchaseCTAHandler from '@hooks/usePurchaseCTAHandler';
import useWindowSize from '@hooks/useWindowSize';
import Head from 'next/head';
import { breakpoints, Spacings, Fonts, mediaQuery } from '@styles/index';
import pushRoute from '@utils/pushRoute';
import useGoalPageStaticContent from '@api/hooks/goal/useGoalPageStaticContent';
import { segment } from '@actions/analytics';
import { CHECKOUT_OFFER_VIEWED, GOAL_PAGE } from '@constants/segment';
import { useEffect } from 'react';
import { abProxyJWT, abVariations } from '@utils/abHelper';
import useExperiments, { setExperimentVariation } from '@stores/experiments';
import { getExperimentVariation } from '@stores/selectors/experiments';
import { ONBOARDING_GTP_FREE_TRY_CLICKED } from '@cont/Onboarding/constants';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getJWTToken } from '@api/selectors/auth';
import { readCookie } from '@utils/cookies';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import usePageHead from './usePageHead';

const wrapperImageBoy = 'goal/boy.svg';
const wrapperImageGirl = 'goal/girl.svg';
const goalMobileImage = 'goal/goal-bg.png';
const discountIcon = 'discount-green.svg';
// const winterLogo = 'winter-logo.svg';

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  background-image: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(229, 244, 255, 0.44) 100%
  );
  background-position: bottom 20px left, bottom 20px right, center;
  background-repeat: no-repeat;
  text-align: center;
  padding-top: 136px;
  padding-bottom: 98px;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    padding-top: 88px;
    background-size: 160px 260px, 160px 260px, 100%;
    background-position: bottom 0px left, bottom 0px right, center;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    padding: 56px 16px 60px 16px;
    text-align: left;
    background: linear-gradient(
      180deg,
      var(--color-i-white) 0%,
      var(--color-i-blue-10) 100%
    );
  }
  z-index: 0;
`;

const WrapperBoyImage = styled.div`
  position: absolute;
  left: 0;
  z-index: -1;
  bottom: 16px;

  ${mediaQuery.uptoTablet} {
    max-width: 17.5vw;

    img {
      object-fit: contain;
    }
  }

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const WrapperGirlImage = styled(WrapperBoyImage)`
  right: 0;
  left: auto;
`;

// const ActiveButton = styled.button`
//   margin-left: auto;
//   margin-right: auto;
//   /* background: linear-gradient(
//     95.84deg,
//     var(--color-i-blue) 0%,
//     var(--color-i-green) 100%
//   ); */
//   background: var(--color-i-winter-logo-blue);
//   display: flex;
//   align-items: center;
//   padding-left: ${Spacings.SPACING_3B};
//   padding-right: ${Spacings.SPACING_3B};
//   border: none;
//   border-radius: 6px;
//   cursor: pointer;
//   @media only screen and (max-width: ${breakpoints.tablet}) {
//     display: none;
//   }
// `;

// const BtnText = styled(P2)`
//   color: var(--color-i-white);
//   padding: 6px 6px 6px 12px;
//   font-weight: 600;
//   font-size: 14px;
//   line-height: 150% !important;
// `;

const Header = styled.div`
  margin: 24px auto 32px auto;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin-bottom: 16px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin: 0;
    text-align: left;
  }
`;

const SubHeaderContainer = styled.div`
  margin: 0px auto 40px auto;
  width: 700px;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 480px;
    margin-bottom: 32px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    margin: 12px 0 24px;
    padding-right: 16px;
  }
`;

const SubHeader = styled(H3)`
  font-weight: normal;
  line-height: 150%;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    font-size: 16px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 14px;
  }
`;

const ButtonWrapper = styled.div`
  display: grid;
  grid-gap: ${Spacings.SPACING_4B};
  grid-auto-flow: column;
  justify-content: center;
  ${mediaQuery.uptoMobile} {
    grid-auto-flow: row;
    justify-content: inherit;
  }
  button:nth-child(2):hover {
    background-color: var(--color-base-1);
  }
`;
const SubButton = styled(Button)`
  margin: auto;
  width: 217px;
  height: 60px;
  font-size: 20px;
  display: inline-block;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    min-height: 48px;
    height: 48px;
    font-size: 16px;
    width: 171px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    margin: 0;
    display: inline-block;
  }
`;

const MobileImageWrapper = styled.div`
  display: none;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    position: absolute;
    right: 0;
    bottom: -24px;
    width: 180px;
    height: 220px;
    display: block;
    z-index: -1;
  }
`;

const Title = styled(H1)`
  width: ${({ $width }) => $width};
  margin: auto !important;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    font-size: 32px;
    line-height: 120%;
    width: 100%;
  }
`;

const OfferWrap = styled.div`
  margin-top: ${Spacings.SPACING_8B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: ${Spacings.SPACING_6B};
  }
`;

const OfferCTACont = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${Spacings.SPACING_3B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    justify-content: flex-start;
  }
`;

const OfferIconWrap = styled.div`
  margin-right: ${Spacings.SPACING_1B};
`;

const StyledUnderlineLink = styled(UnderlineLink)`
  padding-bottom: ${Spacings.SPACING_2};
`;

const LinkText = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
`;

// const PromoWrapper = styled.a`
//   display: inline-block;
// `;

const TITLE_MAX_CHAR = 65;

const ALLOWED_GOAL_TRY_FREE = [
  'PESHE',
  'RTPSX',
  'TMUVD',
  'YOTUH',
  'SDDOC',
  'KSCGY',
  'VLEMN',
  'XNDUS',
  'QJEJG'
];

const PageHead = ({ block, goalUID, goalSlug, goalDetails, isSubscribed }) => {
  const { name, isNoTestGoal } = goalDetails;
  const meJwt = useMyInfo(getJWTToken);
  const { handleSubscriptionClick, isDrawerOpen, closeDrawer } = usePageHead({
    goalSlug,
    goalUID,
    goalDetails,
    isSubscribed
  });
  const isRecordedGoal = useIsRecordedGoal(goalUID);

  const hanldeTryForFree = () => {
    segment.track(ONBOARDING_GTP_FREE_TRY_CLICKED, {
      goal_name: goalDetails.name,
      goal_uid: goalUID,
      last_primary_source: GOAL_PAGE
    });
    const route = {
      href: '/goal/[goalSlug]/[goalUID]/browse',
      as: `/goal/${goalSlug}/${goalUID}/browse`
    };
    pushRoute(route.href, route.as);
  };
  let { goalStaticContent: staticContent } = useGoalPageStaticContent(goalUID);
  let { goalStaticContent: defaultData } =
    useGoalPageStaticContent('__default');

  if (!defaultData)
    defaultData = {
      promoText: 'LISTEN TO OUR NEW ANTHEM',
      promoUrl: 'https://www.youtube.com/watch?v=hz5wsiXWeeo',
      pageTitle: `Crack ${name} with India's largest learning platform`,
      pageSubtitle:
        "Get Plus subscription and access unlimited live and recorded courses from India's best educators"
    };

  if (!staticContent) staticContent = defaultData;
  const offerHeader = block.extraBlockInfo?.offerHeader;
  const { isMobile } = useWindowSize();
  const CTALabel = usePurchaseCTAHandler(goalUID);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const accessToken = readCookie('accessToken');
  const experimentVariation = useExperiments(
    getExperimentVariation('try_for_free_web')
  );
  const isTestVariation =
    experimentVariation && experimentVariation === abVariations.TEST_1;
  const isGoalForExp =
    ALLOWED_GOAL_TRY_FREE.includes(goalUID) || isRecordedGoal;

  useEffect(() => {
    if (isGoalForExp) {
      if (!accessToken && !isLoggedIn) {
        setExperimentVariation('try_for_free_web', null);
        fetchABExperimentData('try_for_free_web', abProxyJWT);
      } else if (accessToken && isLoggedIn) {
        setExperimentVariation('try_for_free_web', null);
        fetchABExperimentData('try_for_free_web', meJwt);
      }
    }

    return () => {
      setExperimentVariation('try_for_free_web', null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, accessToken]);

  let title =
    staticContent.pageTitle ||
    `Crack ${name} with India's largest learning platform`;
  // let subtitle = "India's largest learning platform";
  let subheader =
    staticContent.pageSubtitle ||
    "Get Plus subscription and access unlimited live and recorded courses from India's best educators";

  if (goalUID === 'QZIXN') {
    // subtitle = "Brazil's Top Educators";
    subheader =
      staticContent.pageSubtitle ||
      "Get Plus subscription and access unlimited live and recorded courses from Brazil's best educators";
  }

  if (isNoTestGoal && block?.data[0]) {
    title = block.data[0].name;
    // subtitle = block.data[0].tag_line;
    subheader = block.data[0].description;
  }

  return (
    <>
      {isMobile ? (
        <Head>
          <link rel="preload" as="image" href={getImage('goal/goal-bg.png')} />
        </Head>
      ) : null}
      <Wrapper $hasOffer={!!offerHeader}>
        <WrapperBoyImage>
          <Image
            priority
            src={wrapperImageBoy}
            alt="Boy"
            height={500}
            width={350}
          />
        </WrapperBoyImage>
        <WrapperGirlImage>
          <Image
            priority
            src={wrapperImageGirl}
            alt="Girl"
            height={460}
            width={350}
          />
        </WrapperGirlImage>
        {/* {staticContent.promoText && (
          <PromoWrapper
            href={staticContent.promoUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ActiveButton>
              <Image
                src={winterLogo}
                alt="winter-logo"
                height={24}
                width={24}
              />
              <BtnText>{staticContent.promoText}</BtnText>

              <ChevronRight size="16px" color="var(--color-i-white)" />
            </ActiveButton>
          </PromoWrapper>
        )} */}
        <Header>
          <Title
            $width={title.length > TITLE_MAX_CHAR ? '80%' : '60%'}
          >{`${title} `}</Title>
        </Header>
        <SubHeaderContainer>
          <SubHeader>{subheader}</SubHeader>
        </SubHeaderContainer>
        <ButtonWrapper>
          <SubButton
            label={CTALabel}
            onClick={handleSubscriptionClick}
            dataAnalytics="goal-getSubscription"
          />
          {isTestVariation && (
            <SubButton
              label="Try for free"
              onClick={hanldeTryForFree}
              dataAnalytics="goal-getSubscription"
              variant="hollow"
              color="black"
            />
          )}
        </ButtonWrapper>
        {offerHeader && (
          <OfferWrap>
            <OfferCTACont>
              <OfferIconWrap>
                <Image
                  width={24}
                  height={24}
                  src={discountIcon}
                  alt="discount"
                />
              </OfferIconWrap>
              <StyledUnderlineLink
                color="var(--color-i-green)"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  segment.track(CHECKOUT_OFFER_VIEWED, {
                    goal_name: name,
                    goal_uid: goalUID,
                    is_combo: false
                  });
                  pushRoute(
                    '/goal/[goalSlug]/[goalUID]/offers',
                    `/goal/${goalSlug}/${goalUID}/offers`,
                    {},
                    'goal'
                  );
                }}
              >
                <LinkText color="var(--color-i-green)">
                  View subscription offers
                </LinkText>
              </StyledUnderlineLink>
            </OfferCTACont>
            {!isMobile && (
              <P1 color="var(--color-text-primary)">{offerHeader}</P1>
            )}
          </OfferWrap>
        )}
        <MobileImageWrapper>
          <Image
            src={goalMobileImage}
            alt="Learning"
            width={180}
            height={220}
            priority
          />
        </MobileImageWrapper>
      </Wrapper>
      <AppInstallBanner onClose={closeDrawer} open={isDrawerOpen} />
    </>
  );
};

PageHead.propTypes = {
  goalUID: PropTypes.string,
  goalSlug: PropTypes.string,
  goalDetails: PropTypes.objectOf(PropTypes.any),
  block: PropTypes.objectOf(PropTypes.any),
  isSubscribed: PropTypes.bool
};

PageHead.defaultProps = {
  goalUID: '',
  goalSlug: '',
  goalDetails: {},
  block: {},
  isSubscribed: false
};
export default PageHead;
